import { ChangeDetectorRef, Injectable } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { ObjectHelper } from '@barthauer/abc/core';
import { formatMessage, loadMessages, locale } from 'devextreme/localization';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { AssetsManagerService } from '../../../../assets-manager/src';
import { ResourceSet } from '../resource-set';

@Injectable({
  providedIn: 'root',
})
export class ResourceManagerService {
  private DEV_EXTREME = 'devextreme-messages';
  private resources: Map<
    string,
    Map<string, { subjects: Subject<void>[]; res: object | undefined; resset: ResourceSet }>
  > = new Map<string, Map<string, { subjects: Subject<void>[]; res: object | undefined; resset: ResourceSet }>>();
  private static appliedLanguage: string;
  private dxResourcesLoaded: boolean = false;

  private constructor(
    private assetsManagerService: AssetsManagerService,
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer
  ) {}

  public async getResourceSet(
    resourceSet: string,
    action?: (res: ResourceSet) => void,
    cdr?: ChangeDetectorRef
  ): Promise<ResourceSet> {
    const l = this.applyCurrentLanguage();
    await this.getResourceSetCore(resourceSet, '');
    let res = await this.getResourceSetCore(resourceSet, l);
    if (!res) {
      console.error('ResourceSet not available:' + resourceSet);
    } else {
      if (action) {
        action(res);
      }
      if (cdr) {
        cdr.detectChanges();
      }
    }
    return res;
  }

  public getResourceSetAsObservable(
    resourceSet: string,
    action?: (res: ResourceSet) => void,
    cdr?: ChangeDetectorRef
  ): Observable<ResourceSet> {
    const subject = new ReplaySubject<ResourceSet>(2);
    const notify = (res: ResourceSet) => {
      subject.next(res);
      subject.complete();
    };
    if (action) {
      const firstAction = action;
      action = (res) => {
        firstAction(res);
        notify(res);
      };
    } else {
      action = notify;
    }
    subject.next(ResourceSet.Empty);
    this.getResourceSet(resourceSet, action, cdr);
    return subject.asObservable();
  }

  private async getResourceSetCore(resourceSet: string, l: string): Promise<ResourceSet> {
    const defaultFallbackLanguages = ['en', 'de'];
    let fallbackLanguage: string | undefined = '';
    let namesMap = this.resources.get(l);
    if (!namesMap) {
      namesMap = new Map<string, { subjects: Subject<void>[]; res: object; resset: ResourceSet }>();
      this.resources.set(l, namesMap);
      if (this.resources.size === 1) {
        this.addIconResolver();
      }
    }
    let entry = namesMap.get(resourceSet);
    if (!entry) {
      entry = { subjects: [], res: undefined, resset: ResourceSet.Empty };
      namesMap.set(resourceSet, entry);
    }
    if (!entry.res) {
      const subject = new Subject<void>();
      entry.subjects.push(subject);
      if (entry.subjects.length === 1) {
        const invariantres: any = this.resources.get('')?.get(resourceSet)?.res;
        let res: any;
        let langtoload = l;
        let isDevExtremeDefault = false;
        if (invariantres) {
          const objinvariant = invariantres[''];
          const obj: any = {};
          obj[l] = objinvariant;
          if (resourceSet !== this.DEV_EXTREME) {
            this.dxloadMessages(obj);
          }
          if (langtoload && objinvariant) {
            const languages: string[] = objinvariant[resourceSet + ':$'] ?? objinvariant['$'];
            if (!languages?.length) {
              res = obj;
            } else {
              const found = this.findLanguage(languages, defaultFallbackLanguages, langtoload);
              if (found?.language) {
                fallbackLanguage = found.fallbackLanguage;
              } else {
                if (resourceSet === this.DEV_EXTREME) {
                  isDevExtremeDefault = true;
                } else {
                  langtoload = languages[0];
                  for (const fallback of defaultFallbackLanguages) {
                    const found = this.findLanguage(languages, defaultFallbackLanguages, fallback);
                    if (found?.language) {
                      langtoload = fallback;
                      fallbackLanguage = found.fallbackLanguage;
                      break;
                    }
                  }
                }
              }
            }
          }
        }

        if (!res) {
          if (isDevExtremeDefault) {
            res = await import(`devextreme/localization/messages/${langtoload}.json`);
          } else {
            res = {};
            if (langtoload) {
              res[l] = await this.loadResourceFile(`assets/resources/${resourceSet}.${langtoload}.json`);
            } else {
              res[l] = await this.loadResourceFile(`assets/resources/${resourceSet}.json`);
            }
            if (fallbackLanguage) {
              res[l] = ObjectHelper.mergeDeep(
                await this.loadResourceFile(`assets/resources/${resourceSet}.${fallbackLanguage}.json`),
                res[l]
              );
            }
          }
          if (resourceSet !== this.DEV_EXTREME) {
            const langobj = (res as any)[l];
            const newlangobj: any = {};
            this.appendMessages(newlangobj, langobj, resourceSet + ':');
            (res as any)[l] = newlangobj;
          }
        }
        if (res) {
          if (l) {
            this.dxloadMessages(res);
            entry.resset = new ResourceSet(resourceSet);
          }
          entry.res = res;
          for (const s of entry.subjects) {
            s.next();
            s.complete();
          }
          entry.subjects = [];
        }
      }
      await subject.asObservable().toPromise();
    }
    return entry.resset;
  }

  private findLanguage(
    languages: string[],
    defaultFallbackLanguages: string[],
    langtoload: string
  ): { language: string | undefined; fallbackLanguage: string | undefined } {
    const fallbackSuffix = '?';
    let found = languages.find((_) => _ === langtoload);
    let fallbackLanguage: string | undefined;
    if (!found) {
      found = languages.find((_) => _ === langtoload + fallbackSuffix);
      for (const fallback of defaultFallbackLanguages) {
        fallbackLanguage = languages.find((_) => _ === fallback);
        if (fallbackLanguage) {
          break;
        }
      }
      if (!fallbackLanguage) {
        fallbackLanguage = languages.find((_) => !_.endsWith(fallbackSuffix));
      }
    }
    return { language: found?.replace(fallbackSuffix, ''), fallbackLanguage: fallbackLanguage };
  }

  private dxloadMessages(res: any) {
    loadMessages(res);
  }

  private appendMessages(target: any, source: any, prefix: string) {
    if (!source) {
      return;
    }
    for (const key of Object.keys(source)) {
      const test = source[key];
      if (typeof test === 'object' && !Array.isArray(test)) {
        this.appendMessages(target, test, prefix + key + '-');
      } else {
        target[prefix + key] = test;
      }
    }
  }

  public static getSvgName(resourceSet: string, key: string): string {
    return resourceSet + ':' + key;
  }

  public static getSvgUrl(resourceSet: string, key: string): string {
    const file = this.getFormattedString(resourceSet, key);
    if (!file) {
      return '';
    }

    return `assets/resources/${resourceSet}/${file}`;
  }

  public static getFormattedString(resourceSet: string, key: string, ...values: string[]): string {
    return formatMessage(resourceSet + ':' + key, ...values);
  }

  private addIconResolver() {
    this.iconRegistry.addSvgIconResolver((name, namespace) => {
      const url = ResourceManagerService.getSvgUrl(namespace, name);
      if (url) {
        return this.sanitizer.bypassSecurityTrustResourceUrl(url);
      } else {
        return null;
      }
    });
  }

  public static getFixedCulture(): string | undefined {
    const culture = sessionStorage.getItem('fixedCulture');
    if (culture && culture !== 'null' && culture !== 'undefined') {
      return culture;
    } else {
      return undefined;
    }
  }

  public static getCurrentUserLanguage(): string | null {
    const language = sessionStorage.getItem('currentUserLanguage');
    return language;
  }

  public static setCurrentUserLanguage(language: string) {
    sessionStorage.setItem('currentUserLanguage', language);
  }

  public static setFixedCulture(value: string | undefined): void {
    if (value && value !== 'null' && value !== 'undefined') {
      sessionStorage.setItem('fixedCulture', value);
      ResourceManagerService.setCurrentUserLanguage(value);
    } else {
      sessionStorage.removeItem('fixedCulture');
    }
  }

  private applyCurrentLanguage(): string {
    const l = ResourceManagerService.getCurrentLanguage();
    this.applyLanguageCore(l);
    return l;
  }

  public static tryApplyLanguage(language: string): boolean {
    if (this.appliedLanguage == language) {
      return false;
    }
    locale(language);
    this.appliedLanguage = language;
    return true;
  }

  private applyLanguageCore(language: string) {
    if (ResourceManagerService.tryApplyLanguage(language) || !this.dxResourcesLoaded) {
      this.dxResourcesLoaded = true;
      this.getResourceSet(this.DEV_EXTREME).then((_) => {});
    }
  }

  public changeLanguage(language: string): void {
    const l = ResourceManagerService.getLanguageCode(language);
    ResourceManagerService.setFixedCulture(l);
    this.applyLanguageCore(l);
    this.resources.clear();
  }

  private async loadResourceFile(url: string): Promise<any> {
    return await this.assetsManagerService.getFile(url).toPromise();
  }

  public static getCurrentLanguage(): string {
    const l = this.getLanguageCode(
      ResourceManagerService.getCurrentUserLanguage() ??
        ResourceManagerService.getFixedCulture() ??
        document.documentElement.lang
    );
    return l;
  }

  private static getLanguageCode(l: string): string {
    if (!l) {
      console.warn('Language identifier not defined - using "de"!');
      return 'de';
    }
    return l.split('-')[0];
  }

  public get isRightToLeftEnabled(): boolean {
    return this.applyCurrentLanguage() === 'ar';
  }
}
